import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../api/client';
import {
  MemberWithUser,
  Role,
  CreateFromMemberPayload,
} from '../types/member-roles';

/**
 * Filters members by full name (firstName + lastName) using case-insensitive matching.
 * Returns the full list when query is empty or whitespace-only.
 */
export function filterMembers(
  members: MemberWithUser[],
  query: string,
): MemberWithUser[] {
  if (!query.trim()) return members;

  const normalizedQuery = query.toLowerCase().trim();

  return members.filter((member) => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return fullName.includes(normalizedQuery);
  });
}

/**
 * Custom hook for managing member roles on the UsersPage.
 *
 * Fetches all members with their linked user data, provides search/filter,
 * tracks pending role changes, and exposes save/create-account functions.
 */
export function useMemberRoles() {
  const [members, setMembers] = useState<MemberWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChanges, setPendingChanges] = useState<Map<string, Role>>(
    new Map(),
  );
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient<MemberWithUser[]>('/members');
      setMembers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const statusCode = err?.statusCode;
      const raw = err?.message || '';
      if (!statusCode && (raw.includes('fetch') || raw.includes('network') || raw.includes('Failed to fetch'))) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(raw || 'Failed to fetch members');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const filteredMembers = useMemo(
    () => filterMembers(members, searchQuery),
    [members, searchQuery],
  );

  /**
   * Sets a pending role change for a member.
   * If the new role matches the member's persisted role, the pending change is removed.
   */
  const setPendingRole = useCallback(
    (memberId: string, newRole: Role) => {
      setPendingChanges((prev) => {
        const updated = new Map(prev);
        const member = members.find((m) => m.id === memberId);
        const originalRole = member?.user?.role;

        if (originalRole && newRole === originalRole) {
          updated.delete(memberId);
        } else {
          updated.set(memberId, newRole);
        }

        return updated;
      });
    },
    [members],
  );

  /**
   * Determines a user-friendly error message from an API error response.
   * Maps known backend error messages to clearer descriptions.
   */
  const getErrorMessage = useCallback(
    (err: any, context?: string): string => {
      const raw = err?.message || err?.statusText || '';
      const statusCode = err?.statusCode;

      // Last-organizer rejection
      if (
        statusCode === 403 &&
        raw.toLowerCase().includes('organizer account must exist')
      ) {
        return 'Cannot demote the last organizer. At least one organizer must remain.';
      }

      // Self-role-change rejection
      if (statusCode === 403 && raw.toLowerCase().includes('your own role')) {
        return 'You cannot change your own role.';
      }

      // Duplicate email on account creation
      if (statusCode === 409 && raw.toLowerCase().includes('email already exists')) {
        return 'A user with this email already exists.';
      }

      // Member already linked
      if (statusCode === 409 && raw.toLowerCase().includes('already has a linked account')) {
        return 'This member already has a linked account.';
      }

      // Member not found
      if (statusCode === 404 && raw.toLowerCase().includes('member not found')) {
        return 'Member not found. The member may have been removed.';
      }

      // Network failure (no statusCode means fetch itself failed)
      if (!statusCode && (raw.includes('fetch') || raw.includes('network') || raw.includes('Failed to fetch'))) {
        return context
          ? `Network error while ${context}. Please check your connection and try again.`
          : 'Network error. Please check your connection and try again.';
      }

      // Generic fallback with context
      if (context && raw) {
        return `Failed to ${context}: ${raw}`;
      }

      return raw || 'An unexpected error occurred. Please try again.';
    },
    [],
  );

  /**
   * Saves a pending role change for a member by calling PATCH /users/:userId/role.
   * On success, clears the pending change and updates the local member data.
   * On failure, retains the pending change and sets an error message.
   * For last-organizer rejection, reverts the radio selection to Organizer.
   */
  const saveRole = useCallback(
    async (memberId: string, userId: string) => {
      const newRole = pendingChanges.get(memberId);
      if (!newRole) return;

      const member = members.find((m) => m.id === memberId);
      const memberName = member
        ? `${member.firstName} ${member.lastName}`
        : 'member';

      setSavingIds((prev) => new Set(prev).add(memberId));
      setError(null);

      try {
        await apiClient(`/users/${userId}/role`, {
          method: 'PATCH',
          body: { role: newRole },
        });

        // Update local state with the new role
        setMembers((prev) =>
          prev.map((m) =>
            m.id === memberId && m.user
              ? { ...m, user: { ...m.user, role: newRole } }
              : m,
          ),
        );

        // Clear the pending change on success
        setPendingChanges((prev) => {
          const updated = new Map(prev);
          updated.delete(memberId);
          return updated;
        });
      } catch (err: any) {
        const statusCode = err?.statusCode;
        const raw = err?.message || '';

        // Handle last-organizer rejection: revert radio to Organizer
        if (
          statusCode === 403 &&
          raw.toLowerCase().includes('organizer account must exist')
        ) {
          setPendingChanges((prev) => {
            const updated = new Map(prev);
            updated.delete(memberId);
            return updated;
          });
          setError(
            `Cannot change role for ${memberName}: At least one organizer must remain.`,
          );
          return;
        }

        // Handle self-role-change rejection: revert pending change
        if (statusCode === 403 && raw.toLowerCase().includes('your own role')) {
          setPendingChanges((prev) => {
            const updated = new Map(prev);
            updated.delete(memberId);
            return updated;
          });
          setError('You cannot change your own role.');
          return;
        }

        // For other errors, retain pending change so user can retry
        const errorMsg = getErrorMessage(err, `update role for ${memberName}`);
        setError(errorMsg);
      } finally {
        setSavingIds((prev) => {
          const updated = new Set(prev);
          updated.delete(memberId);
          return updated;
        });
      }
    },
    [pendingChanges, members, getErrorMessage],
  );

  /**
   * Creates a user account for an unlinked member by calling POST /users/from-member.
   * On success, refreshes the members list to reflect the new linked state.
   * Re-throws with a user-friendly message so the modal can display it.
   */
  const createAccount = useCallback(
    async (payload: CreateFromMemberPayload) => {
      setError(null);

      try {
        await apiClient('/users/from-member', {
          method: 'POST',
          body: payload,
        });

        // Refresh members list to get updated linkage
        await fetchMembers();
      } catch (err: any) {
        const friendlyMessage = getErrorMessage(err, 'create account');
        setError(friendlyMessage);
        // Re-throw with the friendly message so the modal can display it
        const rethrown: any = new Error(friendlyMessage);
        rethrown.statusCode = err?.statusCode;
        throw rethrown;
      }
    },
    [fetchMembers, getErrorMessage],
  );

  /**
   * Clears the current error message.
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    members,
    loading,
    error,
    clearError,
    retry: fetchMembers,
    searchQuery,
    setSearchQuery,
    filteredMembers,
    pendingChanges,
    setPendingRole,
    saveRole,
    createAccount,
    savingIds,
  };
}
